<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import HandRaiseVoter from "../shared/HandRaiseVoter.vue";
import breakpoint from "../../../entrypoints/shared/breakpoint";
import OptionReferences from "../shared/OptionReferences.vue";
import { orderVotersByName } from "../../../entrypoints/shared/handraise_helpers";
import { flattenOptions } from "../../../entrypoints/shared/contest_utilities";

const store = useStore();

const votersByName = computed(() => store.getters.handRaiseResultsByVoterName);

// Only 1 contest per VR is enabled in handraise
const options = computed(() => flattenOptions(store.getters.contestsInActiveVotingRound[0].options));

const styleIndex = computed(() => {
  switch (true) {
    case (votersByName.value.length > 20 && votersByName.value.length <= 50): return 1;
    case (votersByName.value.length > 50 && votersByName.value.length <= 100): return 2;
    case (votersByName.value.length > 100 && votersByName.value.length <= 150): return 3;
    case (votersByName.value.length > 150): return 4;
    default: return 0;
  };
});

const styleValue = computed(() => {
  // All the following values are in rem
  const values = {
    // ~150 voters max | Screen resolutions worse than HD
    sm: {
      column: [10, 7, 5, 4, 4],
      row: [4, 2, 1, 0.5, 0.5],
      font: [1, 1, 0.8, 0.8, 0.8],
      padding: [1, 0.5, 0.25, 0.25, 0.25],
    },
    // ~200 voters max | Screen resolutions between HD and QHD
    md: {
      column: [24, 20, 12, 10, 8],
      row: [12, 6, 5, 4, 3.5],
      font: [2, 1.75, 1.5, 1.25, 1],
      padding: [1.5, 1, 0.75, 0.5, 0.5],
    },
    // ~200 voters max | Screen resolutions from QHD to UHD
    lg: {
      column: [28, 24, 18, 14, 12],
      row: [12, 4, 3, 2, 2],
      font: [2, 1.75, 1.5, 1.25, 1],
      padding: [1.5, 1.25, 0.75, 0.5, 0.5],
    },
    // ~200 voters max | Screen resolutions from UHD
    xl: {
      column: [60, 40, 30, 24, 22],
      row: [12, 9, 6, 5.4, 4.75],
      font: [3.5, 2.5, 2, 1.75, 1.6],
      padding: [2, 1.5, 1, 0.5, 0.5],
    },
  };

  
  if (breakpoint.greaterEquals("uhd")) return values.xl;
  if (breakpoint.greater("qhd") && breakpoint.lessEquals("uhd")) return values.lg;
  if (breakpoint.greater("lg") && breakpoint.lessEquals("qhd")) return values.md;
  return values.sm;
});

const gridStyle = computed(() => {
  return {
    template: `
      grid-template-columns: repeat(auto-fill, minmax(${styleValue.value.column[styleIndex.value]}rem, 1fr));
      grid-auto-rows: minmax(${styleValue.value.row[styleIndex.value]}rem, auto);
    `,
    voter: `
      font-size: ${styleValue.value.font[styleIndex.value]}rem;
      padding: ${styleValue.value.padding[styleIndex.value]}rem !important;
    `
  };
});

const referenceSytle = computed(() => {
  return `
    font-size: ${styleValue.value.font[styleIndex.value]}rem;
  `;
});
</script>

<template>
  <OptionReferences
    :options="options"
    :observer-mode="true"
    :customStyle="referenceSytle"
  />
  <section class="d-grid gap-2" :style="gridStyle.template">
    <HandRaiseVoter
      v-for="voter in orderVotersByName(votersByName)"
      :voter="voter"
      :observer-mode="store.getters.contestsInActiveVotingRound[0].maximumVotes === 1"
      :full-background="store.getters.contestsInActiveVotingRound[0].maximumVotes === 1"
      :style="gridStyle.voter"
    />
  </section>
</template>
